<script>
import { mapGetters } from 'vuex';
import PhoneDialForm from './PhoneDialForm.vue';
import PhoneAudioSettings from './PhoneAudioSettings.vue';
import PhoneDialpad from './PhoneDialpad.vue';
import CallerInfo from './CallerInfo.vue';
import ConnectionQualityIndicator from './ConnectionQualityIndicator.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { BUSY_STATES, isNotReadyStatus } from '../helper/phoneHelper';
import {
  applyQueueStatus,
  buildQueueStatusOptions,
} from '../helper/phoneQueueActions';
import { transferTargetLabel } from '../helper/phoneTransfer';

// Beyond this many queue chips the bar no longer fits next to the buttons:
// the rest collapse into a "+N" chip with a dropdown.
const MAX_VISIBLE_QUEUES = 5;

export default {
  name: 'PhoneControls',
  components: {
    PhoneDialForm,
    PhoneAudioSettings,
    PhoneDialpad,
    CallerInfo,
    ConnectionQualityIndicator,
    Icon,
  },
  data() {
    return {
      showDialForm: false,
      phoneNumberToDial: '',
      showAudioSettings: false,
      showTransferInput: false,
      showTransferModes: false,
      transferTarget: '',
      transferTargetSelected: false,
      dialingDurationThreshold: 40,
      dialogQueueContext: null,
      showDialpad: false,
      micLevel: 0,
      speakerLevel: 0,
      audioContext: null,
      micAnalyser: null,
      speakerAnalyser: null,
      micSource: null,
      speakerSource: null,
      levelInterval: null,
      openDropdown: null,
      extensionCheckDone: false,
      extensionCheckTimer: null,
    };
  },
  computed: {
    ...mapGetters({
      phoneConfigured: 'phone/configured',
      currentAgentCreds: 'userPhoneCredentials/getCredentials',
      agents: 'agents/getAgents',
      userStatus: 'phone/getUserStatus',
      status: 'phone/getStatus',
      callDuration: 'phone/getCallDuration',
      dialingDuration: 'phone/getDialingDuration',
      holdStatus: 'phone/getHoldStatus',
      transferPhase: 'phone/getTransferPhase',
      transferMode: 'phone/getTransferMode',
      storeTransferTarget: 'phone/getTransferTarget',
      micMuted: 'phone/getMicMuted',
      speakerSilent: 'phone/getSpeakerSilent',
      streams: 'phone/getStreams',
      iceConnected: 'phone/getIceConnected',
      connectionQuality: 'phone/getConnectionQuality',
      phoneConversation: 'phone/getConversation',
      getCurrentUserID: 'getCurrentUserID',
      getCurrentAccountId: 'getCurrentAccountId',
      currentAccount: 'accounts/getCurrentAccount',
      chromeExtensionAvailable: 'phone/getChromeExtensionAvailable',
      remotePhoneOwner: 'phone/getRemotePhoneOwner',
      connectionFailed: 'phone/getConnectionFailed',
      connectionErrorReason: 'phone/getConnectionErrorReason',
    }),
    // The error dialog is dismissable; without this the widget would go back to
    // a plain "Phone offline" and the agent would have nothing left to read.
    connectionErrorLabel() {
      if (!this.connectionFailed) return '';
      if (this.connectionErrorReason === 'credentials') {
        return this.$t('PHONE_CTRLS.CONNECTION_ERROR.CREDENTIALS_SHORT');
      }
      if (this.connectionErrorReason === 'configuration') {
        return this.$t('PHONE_CTRLS.CONNECTION_ERROR.CONFIGURATION_SHORT');
      }
      return this.$t('PHONE_CTRLS.CONNECTION_ERROR.NETWORK_SHORT');
    },
    // The phone is connected in another tab we can switch to — via the Chrome
    // extension (preferred) or the service worker (clientId). When true we offer
    // a "go to that tab" button instead of Connect; otherwise we fall back to
    // letting the user connect here.
    phoneOwnedByOtherTab() {
      return Boolean(
        this.remotePhoneOwner &&
          (this.chromeExtensionAvailable || this.remotePhoneOwner.clientId)
      );
    },
    showExtensionBanner() {
      // Only prompt once the ping/pong handshake has had time to resolve, so
      // an installed extension never flashes the "not installed" bar.
      return this.extensionCheckDone && !this.chromeExtensionAvailable;
    },
    customAgentPhoneStatuses() {
      return this.currentAccount?.agent_phone_statuses || [];
    },
    conversationId() {
      return this.phoneConversation?.id;
    },
    isDisconnected() {
      return !this.iceConnected || this.connectionQuality === 0;
    },
    micBtnClass() {
      if (this.isDisconnected) return 'disconnected';
      if (this.micMuted) return 'active';
      return '';
    },
    micBtnStyle() {
      if (this.isDisconnected) return {};
      const pct = Math.round(this.micLevel * 100);
      const color = this.micMuted ? '239,68,68' : '156,163,175';
      return {
        background: `linear-gradient(to top, rgba(${color},0.4) ${pct}%, transparent ${pct}%)`,
      };
    },
    speakerBtnClass() {
      if (this.isDisconnected) return 'disconnected';
      if (this.speakerSilent) return 'active';
      return '';
    },
    speakerBtnStyle() {
      if (this.isDisconnected) return {};
      const pct = Math.round(this.speakerLevel * 100);
      const color = this.speakerSilent ? '239,68,68' : '156,163,175';
      return {
        background: `linear-gradient(to top, rgba(${color},0.4) ${pct}%, transparent ${pct}%)`,
      };
    },
    currentAgentLiveData() {
      return this.agents.find(agent => agent.id === this.getCurrentUserID);
    },
    formattedCallDuration() {
      return this.formatDuration(this.callDuration);
    },
    formattedDialingDuration() {
      if (!this.dialingDuration) return '00:00';

      return this.formatDuration(this.dialingDuration);
    },
    dialingDurationClass() {
      if (this.dialingDuration >= this.dialingDurationThreshold) {
        return 'text-red-500';
      }

      if (this.dialingDuration >= this.dialingDurationThreshold / 2) {
        return 'text-orange-500';
      }

      return '';
    },
    holdButtonIcon() {
      return this.holdStatus === 'local'
        ? 'microphone-play'
        : 'microphone-pause';
    },
    consultTargetName() {
      return transferTargetLabel(this.agents, this.storeTransferTarget);
    },
    isConsulting() {
      return this.transferPhase === 'consulting';
    },
    isCompletingTransfer() {
      return this.transferPhase === 'completing';
    },
    // 'completing' keeps the consultation layout (with the buttons disabled)
    // so the panel doesn't jump back to the plain cancel button while the
    // REFER is in flight. Only for the consultation flow - a direct transfer
    // keeps the cancel button it has always had.
    showConsultPanel() {
      return (
        this.transferMode === 'consult' &&
        (this.isConsulting || this.isCompletingTransfer)
      );
    },
    filteredAgents() {
      if (this.transferTargetSelected) {
        return [];
      }
      let result = this.agents.filter(
        agent =>
          agent.phone_username &&
          agent.phone_username !== this.currentAgentCreds.username
      );
      const query = this.transferTarget.trim()?.toLowerCase();
      if (!query || query === '') {
        result = result.filter(agent => agent.phone_status === 'online');
      } else {
        result = result.filter(
          agent =>
            agent.name?.toLowerCase().includes(query) ||
            agent.email?.toLowerCase().includes(query) ||
            agent.phone_username.includes(query)
        );
      }
      result = result.sort((a, b) => {
        if (a.status === 'online' && b.status !== 'online') return -1;
        if (a.status !== 'online' && b.status === 'online') return 1;
        return a.name.localeCompare(b.name);
      });
      return result.slice(0, 4);
    },
    hasQueues() {
      return (this.currentAgentLiveData?.phone_queue_status?.length || 0) > 0;
    },
    queues() {
      return this.currentAgentLiveData?.phone_queue_status || [];
    },
    visibleQueues() {
      if (this.queues.length <= MAX_VISIBLE_QUEUES) return this.queues;
      return this.queues.slice(0, MAX_VISIBLE_QUEUES - 1);
    },
    hiddenQueues() {
      return this.queues.slice(this.visibleQueues.length);
    },
    isDoNotDisturb() {
      // DND is its own boolean flag on the phone credential, set via the `dnd`
      // param of the `PUT .../phone_status` endpoint and broadcast alongside
      // `phone_status`. Independent of the busy/online/offline enum.
      return Boolean(this.currentAgentLiveData?.dnd);
    },
    isCurrentlyOnBreak() {
      if (!this.hasQueues) return false;
      return this.currentAgentLiveData.phone_queue_status.some(queue =>
        isNotReadyStatus(queue.status, this.customAgentPhoneStatuses)
      );
    },
    isCurrentAgentAway() {
      if (this.status === 'offline') return false;

      return this.currentAgentLiveData?.phone_queue_status?.some(queue =>
        isNotReadyStatus(queue.status, this.customAgentPhoneStatuses)
      );
    },
    dialogMessage() {
      const ctx = this.dialogQueueContext;
      if (!ctx) return '';
      if (ctx === 'all') {
        return this.isCurrentlyOnBreak
          ? this.$t('PHONE_CTRLS.QUEUE_BREAK.CONFIRM_READY')
          : this.$t('PHONE_CTRLS.QUEUE_BREAK.CONFIRM_BREAK');
      }
      const onBreak = isNotReadyStatus(
        ctx.status,
        this.customAgentPhoneStatuses
      );
      const label = ctx.name || ctx.number;
      return onBreak
        ? this.$t('PHONE_CTRLS.QUEUE_BREAK.CONFIRM_READY_QUEUE', {
            queue: label,
          })
        : this.$t('PHONE_CTRLS.QUEUE_BREAK.CONFIRM_BREAK_QUEUE', {
            queue: label,
          });
    },
    dialogPrimaryLabel() {
      const ctx = this.dialogQueueContext;
      if (!ctx) return '';
      if (ctx === 'all') return this.$t('PHONE_CTRLS.QUEUE_BREAK.CONFIRM');
      const onBreak = isNotReadyStatus(
        ctx.status,
        this.customAgentPhoneStatuses
      );
      return onBreak
        ? this.$t('PHONE_CTRLS.QUEUE_BREAK.LOGIN_TO_QUEUE')
        : this.$t('PHONE_CTRLS.QUEUE_BREAK.LOGOUT_FROM_QUEUE');
    },
    allQueuesFallbackLabel() {
      const ctx = this.dialogQueueContext;
      if (!ctx || ctx === 'all') return '';
      const onBreak = isNotReadyStatus(
        ctx.status,
        this.customAgentPhoneStatuses
      );
      return onBreak
        ? this.$t('PHONE_CTRLS.QUEUE_BREAK.LOGIN_TO_ALL_QUEUES')
        : this.$t('PHONE_CTRLS.QUEUE_BREAK.LOGOUT_FROM_ALL_QUEUES');
    },
    dialogPrimaryClass() {
      const ctx = this.dialogQueueContext;
      if (!ctx || ctx === 'all') return 'break-status-btn-login';
      const onBreak = isNotReadyStatus(
        ctx.status,
        this.customAgentPhoneStatuses
      );
      return onBreak ? 'break-status-btn-login' : 'break-status-btn-logout';
    },
    queueDropdownOptions() {
      const ctx = this.dialogQueueContext;
      if (!ctx) return [];
      if (ctx === 'all') {
        return this.buildDropdownOptions({
          currentStatus: null,
          isOnBreak: this.isCurrentlyOnBreak,
        });
      }
      return this.buildDropdownOptions({
        currentStatus: ctx.status,
        isOnBreak: isNotReadyStatus(ctx.status, this.customAgentPhoneStatuses),
      });
    },
    allQueuesDropdownOptions() {
      return this.buildDropdownOptions({
        currentStatus: null,
        isOnBreak: this.isCurrentlyOnBreak,
      });
    },
  },
  watch: {
    status(newVal, oldVal) {
      if (newVal !== 'speaking') {
        this.showTransferInput = false;
        this.showTransferModes = false;
        this.showDialpad = false;
      }
      if (newVal === 'speaking' && oldVal !== 'speaking') {
        this.startAudioLevelMonitor();
      } else if (newVal !== 'speaking' && oldVal === 'speaking') {
        this.stopAudioLevelMonitor();
      }
      // A call takes the mic over from a running test, and the popup is a
      // full-screen overlay that would hide the call controls.
      if (['ringing', 'dialing', 'speaking', 'transferring'].includes(newVal)) {
        this.showAudioSettings = false;
      }
    },
    streams: {
      deep: true,
      handler() {
        if (this.status === 'speaking') {
          this.reconnectAudioSources();
        }
      },
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('userPhoneCredentials/get');
    emitter.on(BUS_EVENTS.SHOW_PHONE_DIALER, this.showPhoneDialer);
    emitter.on(BUS_EVENTS.SHOW_PHONE_AUDIO_SETTINGS, this.openAudioSettings);
    this.setPhoneControlsVisible(true);
    // Ping the extension; reveal the install bar only if no pong lands in time.
    this.$store.dispatch('phone/checkChromeExtension');
    this.extensionCheckTimer = setTimeout(() => {
      this.extensionCheckDone = true;
    }, 1500);
  },
  beforeUnmount() {
    emitter.off(BUS_EVENTS.SHOW_PHONE_DIALER, this.showPhoneDialer);
    emitter.off(BUS_EVENTS.SHOW_PHONE_AUDIO_SETTINGS, this.openAudioSettings);
    this.setPhoneControlsVisible(false);
    this.stopAudioLevelMonitor();
    clearTimeout(this.extensionCheckTimer);
  },
  methods: {
    startAudioLevelMonitor() {
      try {
        this.audioContext = new (window.AudioContext ||
          window.webkitAudioContext)();
        this.micAnalyser = this.audioContext.createAnalyser();
        this.micAnalyser.fftSize = 256;
        this.speakerAnalyser = this.audioContext.createAnalyser();
        this.speakerAnalyser.fftSize = 256;
        this.reconnectAudioSources();
        this.updateAudioLevels();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('Unable to setup audio level monitor:', e);
      }
    },
    reconnectAudioSources() {
      if (!this.audioContext) return;
      if (this.micSource) {
        this.micSource.disconnect();
        this.micSource = null;
      }
      if (this.speakerSource) {
        this.speakerSource.disconnect();
        this.speakerSource = null;
      }
      if (this.streams?.local) {
        this.micSource = this.audioContext.createMediaStreamSource(
          this.streams.local
        );
        this.micSource.connect(this.micAnalyser);
      }
      if (this.streams?.remote) {
        this.speakerSource = this.audioContext.createMediaStreamSource(
          this.streams.remote
        );
        this.speakerSource.connect(this.speakerAnalyser);
      }
    },
    getAudioLevel(analyser, dataArray) {
      analyser.getByteTimeDomainData(dataArray);
      let sumSquares = 0;
      for (let i = 0; i < dataArray.length; i += 1) {
        // eslint-disable-next-line security/detect-object-injection
        const normalized = (dataArray[i] - 128) / 128;
        sumSquares += normalized * normalized;
      }
      const rms = Math.sqrt(sumSquares / dataArray.length);
      return Math.min(rms * 9, 1);
    },
    updateAudioLevels() {
      const smoothing = 0.3;
      const bufferLength = this.micAnalyser?.fftSize || 256;
      const dataArray = new Uint8Array(bufferLength);
      this.levelInterval = setInterval(() => {
        if (!this.audioContext) return;
        if (this.micAnalyser && this.micSource) {
          const raw = this.getAudioLevel(this.micAnalyser, dataArray);
          this.micLevel = this.micLevel * (1 - smoothing) + raw * smoothing;
        } else {
          this.micLevel = 0;
        }
        if (this.speakerAnalyser && this.speakerSource) {
          const raw = this.getAudioLevel(this.speakerAnalyser, dataArray);
          this.speakerLevel =
            this.speakerLevel * (1 - smoothing) + raw * smoothing;
        } else {
          this.speakerLevel = 0;
        }
      }, 80);
    },
    stopAudioLevelMonitor() {
      if (this.levelInterval) {
        clearInterval(this.levelInterval);
        this.levelInterval = null;
      }
      if (this.micSource) {
        this.micSource.disconnect();
        this.micSource = null;
      }
      if (this.speakerSource) {
        this.speakerSource.disconnect();
        this.speakerSource = null;
      }
      if (this.audioContext) {
        // Skip an already-closed context and swallow the async rejection so
        // close() never throws "Cannot close a closed AudioContext".
        if (this.audioContext.state !== 'closed') {
          this.audioContext.close().catch(() => {});
        }
        this.audioContext = null;
      }
      this.micAnalyser = null;
      this.speakerAnalyser = null;
      this.micLevel = 0;
      this.speakerLevel = 0;
    },
    setPhoneControlsVisible(value) {
      this.$store.dispatch('phone/setPhoneControlsVisible', value);
    },
    formatDuration(duration) {
      const minutes = Math.floor(duration / 60);
      const seconds = duration % 60;
      const minStr = minutes.toString().padStart(2, '0');
      const secsStr = seconds.toString().padStart(2, '0');

      return `${minStr}:${secsStr}`;
    },
    connectPhone() {
      this.$store.dispatch('phone/requestConnect');
    },
    goToPhoneTab() {
      this.$store.dispatch('phone/focusOwnerTab');
    },
    disconnectPhone() {
      this.$store.dispatch('phone/requestDisconnect');
      this.showDialForm = false;
    },
    answer() {
      this.$store.dispatch('phone/acceptCall');
    },
    hangup() {
      this.$store.dispatch('phone/hangup');
    },
    hold() {
      this.$store.dispatch('phone/toggleHold');
    },
    toggleMute() {
      this.$store.dispatch('phone/toggleMute');
    },
    toggleSilence() {
      this.$store.dispatch('phone/toggleSilence');
    },
    toggleDialForm() {
      this.showDialForm = !this.showDialForm;
    },
    showPhoneDialer(phoneNumber = '') {
      this.phoneNumberToDial = phoneNumber;
      this.showDialForm = true;
    },
    toggleAudioSettings() {
      this.showAudioSettings = !this.showAudioSettings;
    },
    // Opened programmatically when the mic can't be acquired (permission
    // denied / call failed on media access) so the operator can fix it.
    openAudioSettings() {
      this.showAudioSettings = true;
    },
    toggleTransferInput() {
      this.showTransferInput = !this.showTransferInput;
      this.showTransferModes = false;
      if (!this.showTransferInput) {
        this.transferTarget = '';
        this.transferTargetSelected = false;
      }
    },
    toggleTransferModes() {
      if (!this.transferTarget.trim()) {
        return;
      }
      this.showTransferModes = !this.showTransferModes;
    },
    initiateTransfer(mode) {
      if (this.transferTarget.trim()) {
        this.$store.dispatch('phone/transfer', {
          target: this.transferTarget.trim(),
          mode,
        });
        this.showTransferInput = false;
        this.showTransferModes = false;
      }
    },
    completeTransfer() {
      this.$store.dispatch('phone/completeConsultTransfer');
    },
    cancelTransfer() {
      this.$store.dispatch('phone/cancelTransfer');
    },
    transferTargetInputChange() {
      this.transferTargetSelected = false;
      // The toggle button is disabled without a target, so an open menu could
      // not be closed again.
      if (!this.transferTarget.trim()) {
        this.showTransferModes = false;
      }
    },
    selectAgent(agent) {
      this.transferTarget = agent.phone_username;
      this.transferTargetSelected = true;
    },
    getStatusColor(status) {
      switch (status) {
        case 'online':
          return '#43b581'; // green
        case 'busy':
          return '#faa61a'; // yellow
        case 'offline':
        default:
          return '#f04747'; // red
      }
    },
    computeQueueClass(status) {
      if (status === 'READY') {
        return 'status-ready';
      }
      if (status === 'BREAK') {
        return 'status-break';
      }
      if (status === 'LUNCH') {
        return 'status-lunch';
      }
      if (status === 'AWAY') {
        return 'status-away';
      }
      if (status === 'DO_NOT_DISTURB') {
        return 'status-dnd';
      }
      if (status === 'BUSINESS_TRIP') {
        return 'status-trip';
      }
      if (this.customAgentPhoneStatuses.includes(status)) {
        return 'status-custom-break';
      }
      if (isNotReadyStatus(status, this.customAgentPhoneStatuses)) {
        return 'status-not-ready';
      }
      if (BUSY_STATES.includes(status)) {
        return 'status-on-call';
      }
      return 'status-offline';
    },
    openQueueDialog(queue) {
      this.dialogQueueContext = queue;
      this.openDropdown = null;
    },
    cancelDialog() {
      this.dialogQueueContext = null;
      this.openDropdown = null;
    },
    toggleDropdown(name) {
      this.openDropdown = this.openDropdown === name ? null : name;
    },
    closeDropdown(name) {
      if (this.openDropdown === name) this.openDropdown = null;
    },
    // `queueNumber` null applies the status to every queue at once.
    applyStatus(queueNumber, status) {
      return applyQueueStatus({
        accountId: this.getCurrentAccountId,
        agentId: this.getCurrentUserID,
        queues: this.currentAgentLiveData?.phone_queue_status || [],
        customStatuses: this.customAgentPhoneStatuses,
        queueNumber,
        status,
      });
    },
    async confirmDialogAction(pickedStatus = null) {
      const ctx = this.dialogQueueContext;
      this.dialogQueueContext = null;
      this.openDropdown = null;
      if (!ctx) return;

      if (ctx === 'all') {
        await this.applyStatus(
          null,
          pickedStatus || (this.isCurrentlyOnBreak ? 'READY' : 'NOT_READY')
        );
        return;
      }

      const onBreak = isNotReadyStatus(
        ctx.status,
        this.customAgentPhoneStatuses
      );
      await this.applyStatus(
        ctx.number,
        pickedStatus || (onBreak ? 'READY' : 'NOT_READY')
      );
    },
    async confirmAllQueuesAction(pickedStatus = null) {
      const ctx = this.dialogQueueContext;
      this.dialogQueueContext = null;
      this.openDropdown = null;

      const onBreak = isNotReadyStatus(
        ctx.status,
        this.customAgentPhoneStatuses
      );
      await this.applyStatus(
        null,
        pickedStatus || (onBreak ? 'READY' : 'NOT_READY')
      );
    },
    statusLabel(status) {
      if (status === 'READY')
        return this.$t('PHONE_CTRLS.QUEUE_BREAK.LOGIN_TO_QUEUE');
      if (status === 'NOT_READY')
        return this.$t('PHONE_CTRLS.QUEUE_BREAK.OPTION_LOGOUT');
      if (status === 'BREAK')
        return this.$t('PHONE_CTRLS.QUEUE_BREAK.OPTION_BREAK');
      if (status === 'LUNCH')
        return this.$t('PHONE_CTRLS.QUEUE_BREAK.OPTION_LUNCH');
      return status;
    },
    queueShortLabel(queue) {
      const label = queue.name || queue.number;
      return this.queues.length <= 3 ? label : String(label).slice(0, 3);
    },
    queueTooltip(queue) {
      const label = queue.name || queue.number;
      return `${label}: ${queue.status ? this.queueStatusLabel(queue.status) : 'unknown'}`;
    },
    queueStatusLabel(status) {
      if (!status) return '';
      const key = `QUEUE_STATUS.${status}`;
      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      return this.$te(key) ? this.$t(key) : status;
    },
    statusClassName(status) {
      if (status === 'READY') return 'break-status-option-ready';
      if (status === 'NOT_READY') return 'break-status-option-not-ready';
      if (status === 'BREAK') return 'break-status-option-break';
      if (status === 'LUNCH') return 'break-status-option-lunch';
      return 'break-status-option-custom';
    },
    buildDropdownOptions({ currentStatus, isOnBreak }) {
      return buildQueueStatusOptions({
        currentStatus,
        isOnBreak,
        customStatuses: this.customAgentPhoneStatuses,
      }).map(status => ({
        status,
        label: this.statusLabel(status),
        className: this.statusClassName(status),
      }));
    },
    toggleDialpad() {
      this.showDialpad = !this.showDialpad;
    },
    sendDTMF(tone) {
      this.$store.dispatch('phone/sendDTMF', { tone });
    },
    openConversation() {
      this.$router.push({
        name: 'inbox_conversation',
        params: {
          accountId: this.phoneConversation.account_id,
          conversation_id: this.phoneConversation.id,
        },
      });
    },
  },
};
</script>

<template>
  <!-- Hidden on phones: calls need the Chrome extension, which phones lack.
       Only hidden, not removed, so the phone logic still runs. -->
  <div class="max-md:hidden">
    <div
      v-if="phoneConfigured"
      id="phone-controls"
      :class="{ away: isCurrentAgentAway }"
    >
      <a
        v-if="showExtensionBanner"
        href="https://chromewebstore.google.com/detail/jarabot-contact-center-no/oklcddfdeoafjgnnmibbpnccegfaohld"
        target="_blank"
        rel="noopener noreferrer"
        class="extension-banner"
      >
        <span class="extension-banner-text">
          {{ $t('PHONE_CTRLS.EXTENSION_BANNER.TEXT') }}
        </span>
        <span class="extension-banner-cta">
          {{ $t('PHONE_CTRLS.EXTENSION_BANNER.CTA') }}
        </span>
      </a>

      <PhoneDialForm
        v-if="status === 'connected' && showDialForm"
        :initial-phone-number="phoneNumberToDial"
        @close="showDialForm = false"
      />

      <PhoneAudioSettings
        v-if="showAudioSettings"
        @close="showAudioSettings = false"
      />

      <div v-if="showTransferInput">
        <div class="transfer-input-header">
          {{ $t('PHONE_CTRLS.TRANSFER.TITLE') }}
        </div>
        <div class="transfer-input-content">
          <input
            v-model="transferTarget"
            type="text"
            :placeholder="$t('PHONE_CTRLS.TRANSFER.TARGET_PLACEHOLDER')"
            class="transfer-input"
            autocomplete="off"
            @input="transferTargetInputChange"
          />
          <div class="transfer-search-container">
            <div v-if="filteredAgents.length > 0" class="transfer-agents-list">
              <div
                v-for="agent in filteredAgents"
                :key="agent.id"
                class="transfer-agent-item"
                @click="selectAgent(agent)"
              >
                <div
                  class="agent-status-indicator"
                  :style="{
                    backgroundColor: getStatusColor(
                      agent.phone_status ?? 'offline'
                    ),
                  }"
                />
                <div class="agent-name">
                  {{ agent.phone_username }}
                  {{ agent.name ?? agent.email }}
                </div>
              </div>
            </div>
          </div>
          <div class="transfer-actions">
            <div class="transfer-mode-wrap">
              <button
                class="transfer-btn transfer-btn-confirm"
                :disabled="!transferTarget.trim()"
                @click="toggleTransferModes"
              >
                {{ $t('PHONE_CTRLS.TRANSFER.CONFIRM') }}
                <fluent-icon icon="chevron-down" size="12" />
              </button>
              <div v-if="showTransferModes" class="transfer-mode-menu">
                <button
                  class="transfer-mode-item"
                  @click="initiateTransfer('blind')"
                >
                  <fluent-icon icon="share" size="14" />
                  {{ $t('PHONE_CTRLS.TRANSFER.MODE_DIRECT') }}
                </button>
                <button
                  class="transfer-mode-item"
                  @click="initiateTransfer('consult')"
                >
                  <fluent-icon icon="person" size="14" />
                  {{ $t('PHONE_CTRLS.TRANSFER.MODE_CONSULT') }}
                </button>
              </div>
            </div>
            <button
              class="transfer-btn transfer-btn-cancel"
              @click="toggleTransferInput"
            >
              {{ $t('PHONE_CTRLS.TRANSFER.CANCEL') }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="dialogQueueContext">
        <div class="break-status-content">
          <div class="break-status-message">
            {{ dialogMessage }}
          </div>
          <div class="break-status-actions">
            <div
              v-on-clickaway="() => closeDropdown('queue')"
              class="break-status-dropdown"
            >
              <div class="break-status-split" :class="dialogPrimaryClass">
                <button
                  class="break-status-btn break-status-split-main"
                  :class="dialogPrimaryClass"
                  @click="confirmDialogAction()"
                >
                  {{ dialogPrimaryLabel }}
                </button>
                <button
                  class="break-status-btn break-status-split-toggle"
                  :class="dialogPrimaryClass"
                  @click.stop="toggleDropdown('queue')"
                >
                  <fluent-icon icon="chevron-down" size="14" />
                </button>
              </div>
              <ul
                v-if="openDropdown === 'queue'"
                class="break-status-dropdown-menu"
              >
                <li
                  v-for="option in queueDropdownOptions"
                  :key="`queue-drop-${option.status}`"
                  class="break-status-dropdown-item"
                  :class="option.className"
                  @click="confirmDialogAction(option.status)"
                >
                  {{ option.label }}
                </li>
              </ul>
            </div>
            <div
              v-if="dialogQueueContext !== 'all'"
              v-on-clickaway="() => closeDropdown('all')"
              class="break-status-dropdown"
            >
              <div class="break-status-split" :class="dialogPrimaryClass">
                <button
                  class="break-status-btn break-status-split-main"
                  :class="dialogPrimaryClass"
                  @click="confirmAllQueuesAction()"
                >
                  {{ allQueuesFallbackLabel }}
                </button>
                <button
                  class="break-status-btn break-status-split-toggle"
                  :class="dialogPrimaryClass"
                  @click.stop="toggleDropdown('all')"
                >
                  <fluent-icon icon="chevron-down" size="14" />
                </button>
              </div>
              <ul
                v-if="openDropdown === 'all'"
                class="break-status-dropdown-menu"
              >
                <li
                  v-for="option in allQueuesDropdownOptions"
                  :key="`all-drop-${option.status}`"
                  class="break-status-dropdown-item"
                  :class="option.className"
                  @click="confirmAllQueuesAction(option.status)"
                >
                  {{ option.label }}
                </li>
              </ul>
            </div>
            <button
              class="break-status-btn break-status-btn-cancel"
              @click="cancelDialog"
            >
              {{ $t('PHONE_CTRLS.QUEUE_BREAK.CANCEL') }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="status === 'offline'" :class="status">
        <div
          class="status-indicator"
          :class="{ 'cursor-pointer hover:underline': phoneOwnedByOtherTab }"
          @click="phoneOwnedByOtherTab && goToPhoneTab()"
        >
          <fluent-icon icon="phone-off" size="18" icon-lib="tabler" />
          {{
            phoneOwnedByOtherTab
              ? $t('PHONE_CTRLS.STATUS.IN_OTHER_TAB')
              : connectionErrorLabel || $t('PHONE_CTRLS.STATUS.OFFLINE')
          }}
        </div>
        <div v-if="!phoneOwnedByOtherTab" class="action-buttons">
          <button
            v-tooltip.top="$t('PHONE_AUDIO.TITLE')"
            class="phone-btn"
            :class="{ active: showAudioSettings }"
            @click="toggleAudioSettings"
          >
            <Icon icon="i-lucide-settings" />
          </button>
          <button
            v-if="userStatus === 'online'"
            id="phone-disconnect-button"
            v-tooltip.top="$t('PHONE_CTRLS.ACTIONS.DISCONNECT')"
            class="phone-btn hangup"
            @click="disconnectPhone"
          >
            <fluent-icon icon="plug-x" size="18" icon-lib="tabler" />
          </button>
          <button
            v-else
            id="phone-connect-button"
            v-tooltip.top="$t('PHONE_CTRLS.ACTIONS.CONNECT')"
            class="phone-btn answer"
            @click="connectPhone"
          >
            <fluent-icon icon="plug" size="18" icon-lib="tabler" />
          </button>
        </div>
      </div>

      <div v-if="status === 'connecting'" :class="status">
        <div class="status-indicator">
          <span v-if="userStatus === 'online'">
            <fluent-icon icon="plug" size="18" icon-lib="tabler" />
            {{ $t('PHONE_CTRLS.STATUS.CONNECTING') }}
          </span>
          <span v-else>
            <fluent-icon icon="plug-x" size="18" icon-lib="tabler" />
            {{ $t('PHONE_CTRLS.STATUS.DISCONNECTING') }}
          </span>
        </div>
        <div class="action-buttons">
          <button
            v-if="userStatus === 'online'"
            id="phone-disconnect-button"
            v-tooltip.top="$t('PHONE_CTRLS.ACTIONS.DISCONNECT')"
            class="phone-btn hangup"
            @click="disconnectPhone"
          >
            <fluent-icon icon="plug-x" size="18" icon-lib="tabler" />
          </button>
        </div>
      </div>

      <div v-if="status === 'connected'" :class="status">
        <div class="status-and-actions">
          <div
            v-if="hasQueues"
            id="phone-queue-status"
            v-on-clickaway="() => closeDropdown('moreQueues')"
            class="status-indicator-small"
          >
            <div class="status-line">
              <fluent-icon icon="phone" />
              {{ $t('PHONE_CTRLS.STATUS.ONLINE') }}
            </div>
            <div class="queue-status-display">
              <span
                v-if="isDoNotDisturb"
                v-tooltip.top="$t('QUEUE_STATUS.DO_NOT_DISTURB')"
                class="dnd-label"
              >
                {{ $t('PHONE_CTRLS.STATUS.DND') }}
              </span>
              <div
                v-for="queue in visibleQueues"
                :id="`phone-queue-status-${queue.number}`"
                :key="queue.number"
                v-tooltip="queueTooltip(queue)"
                class="queue-status-bubble"
                :class="computeQueueClass(queue.status)"
                @click="openQueueDialog(queue)"
              >
                <span class="truncate-text">
                  {{ queueShortLabel(queue) }}
                </span>
              </div>
              <div
                v-if="hiddenQueues.length"
                id="phone-queue-status-more"
                v-tooltip="hiddenQueues.map(queueTooltip).join(', ')"
                class="queue-status-bubble queue-status-more"
                :aria-label="$t('PHONE_CTRLS.QUEUES.MORE')"
                @click.stop="toggleDropdown('moreQueues')"
              >
                {{ `+${hiddenQueues.length}` }}
              </div>
            </div>
            <ul
              v-if="openDropdown === 'moreQueues'"
              class="break-status-dropdown-menu queue-more-menu"
            >
              <li
                v-for="queue in hiddenQueues"
                :key="`queue-more-${queue.number}`"
                class="queue-more-item"
                @click="openQueueDialog(queue)"
              >
                <span
                  class="queue-status-bubble"
                  :class="computeQueueClass(queue.status)"
                >
                  {{ queue.name || queue.number }}
                </span>
                <span class="queue-more-status">
                  {{ queueStatusLabel(queue.status) }}
                </span>
              </li>
            </ul>
          </div>
          <div v-else id="phone-queue-status" class="status-indicator">
            <fluent-icon icon="phone" />
            {{ $t('PHONE_CTRLS.STATUS.ONLINE') }}
          </div>
          <div class="action-buttons action-buttons--compact">
            <button
              v-tooltip.top="$t('PHONE_AUDIO.TITLE')"
              class="phone-btn phone-btn--compact"
              :class="{ active: showAudioSettings }"
              @click="toggleAudioSettings"
            >
              <Icon icon="i-lucide-settings" />
            </button>
            <button
              v-tooltip.top="$t('PHONE_DIAL.ACTIONS.DIAL')"
              class="phone-btn phone-btn--compact"
              :class="{ active: showDialForm }"
              @click="toggleDialForm"
            >
              <fluent-icon icon="phone-plus" size="18" icon-lib="tabler" />
            </button>
            <button
              id="phone-disconnect-button"
              v-tooltip.top="$t('PHONE_CTRLS.ACTIONS.DISCONNECT')"
              class="phone-btn phone-btn--compact hangup"
              @click="disconnectPhone"
            >
              <fluent-icon icon="plug-x" size="18" icon-lib="tabler" />
            </button>
          </div>
        </div>
      </div>

      <div v-if="status === 'dialing'" :class="status">
        <div class="caller-info">
          <div>{{ $t('PHONE_CTRLS.STATUS.DIALING') }}</div>
          <CallerInfo />
          <span class="dialing-duration" :class="dialingDurationClass">{{
            formattedDialingDuration
          }}</span>
        </div>
        <div class="action-buttons">
          <button
            v-tooltip.top="$t('PHONE_AUDIO.TITLE')"
            class="phone-btn"
            :class="{ active: showAudioSettings }"
            @click="toggleAudioSettings"
          >
            <Icon icon="i-lucide-settings" />
          </button>
          <button
            v-tooltip.top="$t('PHONE_CTRLS.ACTIONS.HANGUP')"
            class="phone-btn hangup"
            @click="hangup"
          >
            <span class="hangup-icon">
              <fluent-icon icon="call" />
            </span>
          </button>
        </div>
      </div>

      <div v-if="status === 'ringing'" :class="status">
        <div class="caller-info">
          <CallerInfo />
        </div>
        <div class="action-buttons">
          <button
            v-tooltip.top="$t('PHONE_AUDIO.TITLE')"
            class="phone-btn"
            :class="{ active: showAudioSettings }"
            @click="toggleAudioSettings"
          >
            <Icon icon="i-lucide-settings" />
          </button>
          <button
            v-tooltip.top="$t('PHONE_CTRLS.ACTIONS.ANSWER')"
            class="phone-btn answer flashing"
            @click="answer"
          >
            <fluent-icon icon="call" />
          </button>
          <button
            v-tooltip.top="$t('PHONE_CTRLS.ACTIONS.HANGUP')"
            class="phone-btn hangup"
            @click="hangup"
          >
            <span class="hangup-icon">
              <fluent-icon icon="call" />
            </span>
          </button>
        </div>
      </div>

      <div v-if="status === 'busy'" class="busy">
        <div class="status-indicator busy-indicator">
          <fluent-icon icon="phone-off" size="18" icon-lib="tabler" />
          {{ $t('PHONE_CTRLS.STATUS.BUSY') }}
        </div>
      </div>

      <div v-if="status === 'unavailable'" class="busy">
        <div class="status-indicator busy-indicator">
          <fluent-icon icon="phone-off" size="18" icon-lib="tabler" />
          {{ $t('PHONE_CTRLS.STATUS.UNAVAILABLE') }}
        </div>
      </div>

      <div v-if="status === 'rejected'" class="busy">
        <div class="status-indicator busy-indicator">
          <fluent-icon icon="phone-off" size="18" icon-lib="tabler" />
          {{ $t('PHONE_CTRLS.STATUS.REJECTED') }}
        </div>
      </div>

      <div v-if="status === 'call-failed'" class="busy">
        <div class="status-indicator busy-indicator">
          <fluent-icon icon="phone-off" size="18" icon-lib="tabler" />
          {{ $t('PHONE_CTRLS.STATUS.CALL_FAILED') }}
        </div>
      </div>

      <div
        v-if="status === 'transferring'"
        :class="[status, { consulting: showConsultPanel }]"
      >
        <div class="status-indicator">
          <fluent-icon
            :icon="isConsulting ? 'person' : 'share'"
            size="18"
            icon-lib="tabler"
          />
          {{
            isConsulting
              ? $t('PHONE_CTRLS.STATUS.CONSULTING')
              : $t('PHONE_CTRLS.STATUS.TRANSFERRING')
          }}
        </div>
        <div v-if="consultTargetName" class="transfer-target-name">
          {{ consultTargetName }}
        </div>
        <div v-if="isConsulting" class="transfer-hint">
          {{ $t('PHONE_CTRLS.STATUS.CALLER_ON_HOLD') }}
        </div>
        <div class="action-buttons">
          <template v-if="showConsultPanel">
            <button
              v-tooltip.top="$t('PHONE_CTRLS.ACTIONS.COMPLETE_TRANSFER')"
              class="phone-btn transfer-complete"
              :disabled="isCompletingTransfer"
              @click="completeTransfer"
            >
              <fluent-icon icon="share" />
            </button>
            <button
              v-tooltip.top="$t('PHONE_CTRLS.ACTIONS.RETURN_TO_CALLER')"
              class="phone-btn return-to-caller"
              :disabled="isCompletingTransfer"
              @click="cancelTransfer"
            >
              <fluent-icon icon="arrow-reply" />
            </button>
          </template>
          <button
            v-else
            v-tooltip.top="$t('PHONE_CTRLS.ACTIONS.CANCEL_TRANSFER')"
            class="phone-btn hangup"
            @click="cancelTransfer"
          >
            <fluent-icon icon="dismiss" />
          </button>
        </div>
      </div>

      <div
        v-if="status === 'speaking'"
        :class="status"
        class="speaking-wrapper"
      >
        <span
          v-if="conversationId"
          v-tooltip.top="$t('PHONE_CTRLS.ACTIONS.OPEN_CONVERSATION')"
          class="open-conversation-icon"
          @click="openConversation"
        >
          <fluent-icon icon="chat" size="14" />
        </span>
        <div class="caller-info">
          <CallerInfo />
          <span class="call-duration">{{ formattedCallDuration }}</span>
          <ConnectionQualityIndicator />
        </div>

        <PhoneDialpad v-if="showDialpad" @dtmf-tone="sendDTMF" />

        <div class="action-buttons">
          <div class="phone-btn-split">
            <button
              v-tooltip.top="$t('PHONE_CTRLS.ACTIONS.MUTE')"
              class="phone-btn"
              :class="micBtnClass"
              :style="micBtnStyle"
              @click="toggleMute"
            >
              <fluent-icon :icon="micMuted ? 'microphone-off' : 'microphone'" />
            </button>
            <button
              v-tooltip.top="$t('PHONE_AUDIO.TITLE')"
              class="phone-btn-caret"
              :class="{ active: showAudioSettings }"
              @click.stop="toggleAudioSettings"
            >
              <fluent-icon icon="chevron-down" size="12" />
            </button>
          </div>
          <div class="phone-btn-split">
            <button
              v-tooltip.top="$t('PHONE_CTRLS.ACTIONS.SILENCE')"
              class="phone-btn"
              :class="speakerBtnClass"
              :style="speakerBtnStyle"
              @click="toggleSilence"
            >
              <fluent-icon
                :icon="speakerSilent ? 'speaker-mute' : 'speaker-1'"
              />
            </button>
            <button
              v-tooltip.top="$t('PHONE_AUDIO.TITLE')"
              class="phone-btn-caret"
              :class="{ active: showAudioSettings }"
              @click.stop="toggleAudioSettings"
            >
              <fluent-icon icon="chevron-down" size="12" />
            </button>
          </div>
          <button
            v-tooltip.top="$t('PHONE_CTRLS.ACTIONS.HOLD')"
            class="phone-btn hold"
            :disabled="holdStatus === 'remote'"
            @click="hold"
          >
            <fluent-icon :icon="holdButtonIcon" />
          </button>
          <button
            v-tooltip.top="$t('PHONE_CTRLS.ACTIONS.TRANSFER')"
            class="phone-btn"
            :class="{
              active: showTransferInput,
            }"
            @click="toggleTransferInput"
          >
            <fluent-icon icon="share" />
          </button>
          <button
            v-tooltip.top="$t('PHONE_CTRLS.ACTIONS.DIALPAD')"
            class="phone-btn"
            :class="{
              active: showDialpad,
            }"
            @click="toggleDialpad"
          >
            <fluent-icon icon="dialpad" />
          </button>
          <button
            v-tooltip.top="$t('PHONE_CTRLS.ACTIONS.HANGUP')"
            class="phone-btn hangup"
            @click="hangup"
          >
            <span class="hangup-icon">
              <fluent-icon icon="call" />
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import 'dashboard/assets/scss/_mixins.scss';

#phone-controls {
  @include border-normal-top();
  @include background-ultralight();
  position: relative;
  padding: 12px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;

  &.away {
    background-color: rgba(250, 166, 26, 0.25);
    border-top: 3px solid #faa61a;
  }
}

// Mic / speaker buttons gain a small caret that opens the audio-device popup.
// Main button area keeps its default action (mute / silence); caret = settings.
.phone-btn-split {
  position: relative;
  display: inline-flex;
  margin: 0 5px;

  .phone-btn {
    margin: 0;
  }
}

.phone-btn-caret {
  @include background-white();
  position: absolute;
  right: -2px;
  bottom: -2px;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 2px solid #a0a0a0;
  border-radius: 50%;
  color: #a0a0a0;
  cursor: pointer;
  transition: all 0.2s;

  &:hover,
  &.active {
    border-color: #007bff;
    color: #007bff;
    transform: scale(1.1);
  }
}

.dark .phone-btn-caret {
  background-color: #36393f;
  color: #dcddde;
}

.extension-banner {
  // High-contrast bar: must stand out against the muted widget chrome.
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: -12px -12px 12px;
  padding: 10px 12px;
  background-color: #b91c1c;
  color: #ffffff;
  text-decoration: none;
  border-bottom: 2px solid #7f1d1d;
  cursor: pointer;

  &:hover {
    background-color: #991b1b;
  }
}

.extension-banner-text {
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
}

.extension-banner-cta {
  font-size: 12px;
  font-weight: 700;
  text-decoration: underline;
}

.phone-btn {
  @include background-white();
  border: 2px solid #a0a0a0;
  color: #a0a0a0;
  padding: 12px;
  border-radius: 50%;
  margin: 0 5px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;

  &:hover {
    transform: scale(1.1);
  }
}

.dark {
  .phone-btn {
    background-color: #36393f;
    color: #dcddde;
  }
}

.phone-btn:hover,
.ringing .phone-btn {
  &.answer {
    border: 2px solid #43b581;
    color: #43b581;
  }
  &.hangup,
  &.active {
    border: 2px solid #f04747;
    color: #f04747;
  }
  &.hold {
    border: 2px solid #faa61a;
    color: #faa61a;
  }
}

.phone-btn.disconnected {
  border: 2px solid #f04747;
  color: #f04747;
}

.phone-btn.transferring {
  border: 2px solid #007bff;
  color: #007bff;
  animation: pulse 1.5s infinite;

  svg {
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.connected,
.connecting,
.transferring,
.offline,
.busy {
  display: flex;
  align-items: center;
  justify-content: center;
}

.status-indicator {
  padding: 12px;
}

.status-indicator svg {
  display: inline;
}

.status-indicator .connected svg,
.status-indicator .connecting svg {
  color: red;
}

.status-indicator .offline svg {
  color: green;
}

.status-action {
  margin-left: 20px;
}

.busy-indicator {
  color: #f04747;
  font-weight: 500;
}

.hangup-icon {
  transform: rotate(135deg);
}

.caller-info {
  text-align: center;
  margin-bottom: 10px;
  white-space: pre-line;
}

.speaking-wrapper {
  position: relative;
}

.open-conversation-icon {
  position: absolute;
  top: 0;
  right: 0;
  cursor: pointer;
  color: $success-color;
  display: inline-flex;

  &:hover {
    opacity: 0.7;
  }
}

.action-buttons {
  display: flex;
  justify-content: space-around;
}

// Connected bar shares one row with the queue chips, so it never shrinks
// and its buttons are smaller than in the other states.
.action-buttons--compact {
  flex-shrink: 0;
}

.phone-btn--compact {
  width: 40px;
  height: 40px;
  padding: 10px;
  margin: 0 4px;
}

.call-duration {
  font-size: 80%;
  width: 5em;
}

.dialing-duration {
  font-size: 80%;
  width: 5em;
}

.flashing {
  animation: flash 1s infinite;
}

@keyframes flash {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.transfer-input-header {
  font-weight: bold;
  margin-bottom: 0.4em;
  text-align: center;
}

.transfer-input-content {
  display: flex;
  flex-direction: column;
  gap: 0.4em;
  margin-bottom: 0.5em;

  .transfer-input {
    @include background-white();
    @include border-light();
    margin-bottom: 0 !important;

    &:focus {
      outline: none;
      @include border-normal();
    }
  }

  .transfer-agent-item {
    display: flex;
    align-items: center;
    justify-content: start;
    padding: 0.3em 0.5em;
    cursor: pointer;

    &:hover {
      @include background-light();
    }
  }

  .agent-info {
    flex: 1;
  }

  .agent-status-indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 0.5em;
  }

  .dark .transfer-agent-item {
    border-bottom-color: #4f545c;
    &:hover {
      background-color: #40444b;
    }
  }
}

.transfer-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.transfer-btn {
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &.transfer-btn-confirm {
    background-color: $success-color;
    color: white;

    &:hover {
      opacity: 0.9;
    }
  }

  &.transfer-btn-cancel {
    @include background-light();
    color: #666;

    &:hover {
      @include background-gray();
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.transfer-mode-wrap {
  position: relative;

  .transfer-btn-confirm {
    display: flex;
    align-items: center;
    gap: 4px;
  }
}

.transfer-mode-menu {
  @include background-white();
  @include border-light();
  position: absolute;
  bottom: calc(100% + 4px);
  left: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  min-width: 200px;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.transfer-mode-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border: none;
  background: transparent;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  color: inherit;

  &:hover {
    @include background-light();
  }
}

.transferring.consulting {
  flex-direction: column;
}

.transfer-target-name {
  font-weight: 600;
  text-align: center;
}

.transfer-hint {
  font-size: 12px;
  color: #a0a0a0;
  text-align: center;
  margin-bottom: 6px;
}

.phone-btn.transfer-complete {
  border: 2px solid #43b581;
  color: #43b581;
}

.phone-btn.return-to-caller {
  border: 2px solid #faa61a;
  color: #faa61a;
}

.phone-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.dark .transfer-input {
  background-color: #2f3136;
  border-color: #4f545c;
  color: #dcddde;
}

.status-and-actions {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.status-indicator-small {
  position: relative;
  min-width: 0;
  font-size: 14px;
  color: #666;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-right: 10px;
}

.status-line {
  display: flex;
  align-items: center;
  gap: 4px;
}

.status-line svg {
  display: inline;
  width: 14px;
  height: 14px;
}

.dark .status-indicator-small {
  color: #aaa;
}

.queue-status-display {
  margin: 4px 0 0 0;
  display: flex;
  flex-wrap: nowrap;
  gap: 0;
  justify-content: flex-start;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.8;
  }
}

.queue-status-bubble.queue-status-more {
  background-color: #8a8f98; /* neutral grey */
  font-weight: 700;
}

.queue-more-menu {
  right: auto;
  min-width: 100%;
  max-height: 240px;
  overflow-y: auto;
}

.queue-more-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 6px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 12px;
  white-space: nowrap;

  &:hover {
    @include background-gray();
  }

  .queue-status-bubble {
    border-radius: 4px;
  }
}

.queue-more-status {
  color: #666;
}

.dark .queue-more-status {
  color: #aaa;
}

.dnd-label {
  display: inline-flex;
  align-items: center;
  align-self: center;
  margin-right: 4px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: #f04747; /* red */
  white-space: nowrap;
}

.queue-status-bubble {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 6px;
  border-radius: 0;
  font-size: 9px;
  font-weight: 500;
  color: white;
  cursor: pointer;
  min-width: 20px;
  text-align: center;

  &:not(:first-child) {
    margin-left: 1px;
  }
  &:first-child {
    border-bottom-left-radius: 4px;
    border-top-left-radius: 4px;
  }
  &:last-child {
    border-bottom-right-radius: 4px;
    border-top-right-radius: 4px;
  }
}

.queue-status-bubble.status-ready {
  background-color: #43b581; /* green */
}

.queue-status-bubble.status-not-ready {
  background-color: #faa61a; /* yellow */
}

.queue-status-bubble.status-break {
  background-color: #d97706; /* dark orange */
}

.queue-status-bubble.status-lunch {
  background-color: #fb923c; /* light orange */
}

.queue-status-bubble.status-away {
  background-color: #f59e0b; /* amber (not-ready variant) */
}

.queue-status-bubble.status-dnd {
  background-color: #ea580c; /* orange (not-ready variant) */
}

.queue-status-bubble.status-trip {
  background-color: #ca8a04; /* yellow (not-ready variant) */
}

.queue-status-bubble.status-custom-break {
  background-color: #b45309; /* amber */
}

.queue-status-bubble.status-on-call {
  background-color: #007bff; /* blue */
}

.queue-status-bubble.status-offline {
  background-color: #f04747; /* red */
}

.truncate-text {
  max-width: 5em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}

.break-status-content {
  display: flex;
  flex-direction: column;
  gap: 0.4em;
  margin-bottom: 0.5em;
}

.break-status-message {
  text-align: center;
  margin: 1em 0;
  font-size: 14px;
}

.break-status-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.break-status-btn {
  padding: 8px 4px 8px 8px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;

  &.break-status-btn-login {
    background-color: $success-color;
    color: white;

    &:hover {
      opacity: 0.9;
    }
  }

  &.break-status-btn-logout {
    background-color: #faa61a;
    color: white;

    &:hover {
      opacity: 0.9;
    }
  }

  &.break-status-btn-cancel {
    @include background-light();
    color: #666;

    &:hover {
      @include background-gray();
    }
  }
}

.break-status-dropdown {
  position: relative;
}

.break-status-split {
  display: inline-flex;
  border-radius: 4px;
  overflow: hidden;
}

.break-status-split-main {
  border-radius: 0;
}

.break-status-split-toggle {
  border-radius: 0;
  padding: 8px 6px 8px 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.break-status-dropdown-menu {
  list-style: none;
  margin: 0 0 4px 0;
  padding: 4px;
  border-radius: 4px;
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 2px;
  @include background-white();
  @include border-light();
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
}

.dark .break-status-dropdown-menu {
  background-color: #2f3136;
  border-color: #4f545c;
}

.break-status-dropdown-item {
  padding: 6px 10px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 13px;
  color: white;
  transition: all 0.2s;
  text-align: center;

  &:hover {
    opacity: 0.9;
  }

  &.break-status-option-ready {
    background-color: $success-color;
  }

  &.break-status-option-not-ready {
    background-color: #faa61a;
  }

  &.break-status-option-break {
    background-color: #d97706;
  }

  &.break-status-option-lunch {
    background-color: #fb923c;
  }

  &.break-status-option-custom {
    background-color: #b45309;
  }
}
</style>
